import { useState } from 'react';
import type { KanbanSortMode } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import { Tooltip } from '@/shared/components/ui/Tooltip';

/** Labels centrais dos 6 modos de ordenação do kanban. Cada módulo passa via `modes` o subconjunto suportado. */
const MODE_LABELS: Record<KanbanSortMode, string> = {
  created: 'Criação (mais novos primeiro)',
  stepEntered: 'Entrada na etapa (mais tempo primeiro)',
  priority: 'Prioridade (urgente primeiro)',
  idle: 'Ociosidade (sem interação há mais tempo)',
  inactivity: 'Inatividade (sem atividade há mais tempo)',
  manual: 'Personalizado (arraste para ordenar)',
};

interface KanbanSortSelectorProps {
  value: KanbanSortMode;
  onChange: (mode: KanbanSortMode) => void;
  /** Subconjunto de modos exibidos no dropdown (ex: `LEAD_KANBAN_SORT_MODES`, `TICKET_KANBAN_SORT_MODES`). */
  modes: readonly KanbanSortMode[];
  /** Classes extras no botão (pass-through). */
  className?: string;
}

/**
 * Seletor do modo de ordenação do kanban (shared — SP1/SP2 leads, SP3 tickets).
 * Visual FIEL ao mockup app.html `.btn` (btnOrdenar): 36px de altura, padding
 * 0 12px, borda border-strong, bg surface, fs 13 medium, rótulo fixo "Ordenar".
 * F60: rótulo SEMPRE fixo "Ordenar" em todos os kanbans (decisão do dono —
 * o antigo `labelMode='value'` foi removido). Dropdown tema claro fixo (regra),
 * modo ativo com radio.
 *
 * Rótulo colapsável no padrão do DS (2026-08-04): `hidden 2xl:@tb:inline` —
 * visível só em viewport ≥2xl E toolbar-container ≥ token `tb` (68rem).
 */
export function KanbanSortSelector({ value, onChange, modes, className }: KanbanSortSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <Tooltip content={`Ordenação: ${MODE_LABELS[value]}`}>
        <button
          data-testid="kanban-sort-selector"
          onClick={() => setIsOpen(!isOpen)}
          className={`${className ?? ''} inline-flex items-center gap-[7px] h-[38px] px-2.5 2xl:@tb:px-3 rounded-lg text-[13px] whitespace-nowrap transition-colors font-medium`}
          style={{
            border: '1px solid var(--theme-input-border)',
            backgroundColor: 'var(--theme-card-background)',
            color: 'var(--theme-card-text)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-card-background)'; }}
        >
          <Icon name="list-ordered" className="h-3.5 w-3.5" style={{ color: 'var(--theme-muted-text)' }} />
          <span className="hidden 2xl:@tb:inline">Ordenar</span>
        </button>
      </Tooltip>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-[100]" onClick={() => setIsOpen(false)} />
          {/* `.pop` do mockup (ddOrdenar): pop-lbl 10px uppercase + `.dd-radio` 14px c/ radio 16 roxo */}
          <div
            className="absolute right-0 mt-2 w-72 rounded-lg shadow-lg z-[101] p-1.5"
            style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}
          >
            <div
              className="font-bold uppercase"
              style={{ fontSize: 10, letterSpacing: '.05em', color: '#94a3b8', padding: '8px 10px 4px' }}
            >
              Ordenar por
            </div>
            {modes.map((mode) => (
              <button
                key={mode}
                data-testid={`kanban-sort-option-${mode}`}
                onClick={() => {
                  onChange(mode);
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2.5 text-left rounded-md transition-colors hover:bg-[#f1f5f9]"
                style={{ padding: '8px 10px', fontSize: 14, color: '#334155' }}
              >
                <span
                  className="flex-none rounded-full"
                  style={{
                    width: 16, height: 16,
                    border: mode === value ? '5px solid #8b5cf6' : '1.5px solid #cbd5e1',
                    backgroundColor: '#ffffff',
                  }}
                />
                {MODE_LABELS[mode]}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
